/**
 * Authentication Service for Login UI
 *
 * BFF (Backend-For-Frontend) Pattern Implementation:
 * - JWT tokens are stored server-side in Redis (not in browser localStorage)
 * - Browser receives opaque session ID in HttpOnly cookie (agion_sso_session)
 * - APISIX forward-auth plugin resolves session → JWT for API requests
 * - Client only needs to send cookies via credentials: 'include'
 *
 * Security Benefits:
 * - JWT tokens never exposed to JavaScript (XSS protection)
 * - Session ID is opaque and not useful without server-side session store
 * - Automatic token refresh handled server-side
 * - Cookie attributes (HttpOnly, Secure, SameSite) enforced by backend
 */

import {
  LoginFormData,
  RegisterFormData,
  ForgotPasswordFormData,
  ResetPasswordFormData,
  MFAFormData,
  ConsentData,
  AuthChallenge,
  ConsentChallenge,
  AuthResponse,
  MFAMethod,
  UIConfig,
} from '@/types/auth';
import { config } from '@/utils/config';
import { registerWithInvitation } from './invitation-registration';
import { AuthRequestError } from './auth-errors';
import { fetchRetryingWhenBusy } from './busy-retry';

interface CreateOrganizationPayload {
  name: string;
  slug?: string;
  description?: string;
  website?: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  plan: string;
  requireMFA: boolean;
}

/**
 * The invitation accept answer (POST /api/v1/invitations/accept): the
 * organisation joined and that membership's home as auth-service decides it
 * (ADR-20260930 D7). Each field is absent when the server names none.
 */
export interface InvitationAcceptance {
  organizationId?: string
  homeApp?: string
  homeUrl?: string
}

export class AuthService {
  // Auth Service URLs (OIDC 1.0 Provider)
  private readonly authServiceUrl: string;
  private readonly apiUrl: string;

  constructor() {
    this.authServiceUrl = config.authServiceUrl;
    this.apiUrl = config.apiUrl;
  }

  /**
   * Get login challenge details from OIDC auth-service
   */
  async getLoginChallenge(challenge: string): Promise<AuthChallenge> {
    try {
      // Use unified gateway endpoint for OIDC admin API
      const response = await fetch(`${this.authServiceUrl}/oauth2/login/requests/${challenge}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Failed to get login challenge: ${response.statusText}`)
      }

      const data = await response.json()

      return {
        challenge,
        csrfToken: '',
        requestUrl: data.request_url || '',
        skip: data.skip || false,
        subject: data.subject || '',
        client: {
          id: data.client.client_id,
          name: data.client.client_name || data.client.client_id,
          description: data.client.description || '',
          logo_uri: data.client.logo_uri,
          policy_uri: data.client.policy_uri,
          tos_uri: data.client.tos_uri,
        },
        oidcContext: data.oidc_context || {},
      }
    } catch (error) {
      console.error('Failed to get login challenge:', error)
      throw new Error('Failed to get login challenge')
    }
  }

  /**
   * Submit login credentials to Auth Service (custom OIDC provider)
   */
  async submitLogin(data: LoginFormData & { challenge: string; csrfToken: string }): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.authServiceUrl}/oauth2/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': data.csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          challenge: data.challenge,
          email: data.email,
          password: data.password,
          remember: data.remember,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Authentication failed')
      }

      const result = await response.json()

      // Handle redirect response from Auth Service
      if (result.redirect_to) {
        return {
          redirect_to: result.redirect_to,
          success: true,
        }
      }

      // Handle MFA required
      if (result.mfa_required) {
        return {
          success: false,
          mfa_required: true,
          mfa_token: result.mfa_token,
          mfa_methods: result.mfa_methods || ['totp'],
        }
      }

      throw new Error('Unexpected response format')
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  /**
   * Register new user
   */
  async register(data: RegisterFormData & { csrfToken: string }): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.authServiceUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          first_name: data.firstName,
          last_name: data.lastName,
          terms: data.terms,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Registration failed')
      }

      const result = await response.json()

      return {
        success: true,
        user: result.user,
        message: result.message || 'Registration successful',
      }
    } catch (error) {
      console.error('Registration failed:', error)
      throw error
    }
  }

  async createOrganization(data: CreateOrganizationPayload): Promise<{ success: boolean; organization?: unknown; error?: string }> {
    try {
      // BFF Pattern: No need to fetch or send JWT tokens
      // Session cookie (agion_sso_session) is sent automatically with credentials: 'include'
      // APISIX forward-auth plugin resolves session → JWT for backend
      const response = await fetch(`${this.apiUrl}/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send session cookie
        body: JSON.stringify({
          name: data.name,
          slug: data.slug,
          description: data.description,
          website: data.website,
          contact_email: data.contactEmail,
          contact_phone: data.contactPhone,
          address: data.address,
          city: data.city,
          state: data.state,
          country: data.country,
          postal_code: data.postalCode,
          plan: data.plan || 'free',
          require_mfa: data.requireMFA,
        }),
      })

      if (!response.ok) {
        let message = 'Failed to create organization'
        try {
          const errorData = await response.json()
          if (errorData?.error) {
            message = errorData.error
          }
        } catch {
          // ignore JSON parse errors and use default message
        }

        return {
          success: false,
          error: message,
        }
      }

      const organization = await response.json()
      return {
        success: true,
        organization,
      }
    } catch (error) {
      console.error('Organization creation failed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create organization',
      }
    }
  }

  /**
   * Logout user — BFF pattern
   * Calls POST /session/logout on auth-service to delete Redis session and clear cookie.
   * Must use credentials: 'include' so the agion_sso_session cookie is sent.
   */
  async logout(): Promise<void> {
    try {
      const response = await fetch(`${this.authServiceUrl}/session/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Logout failed: ${response.statusText}`)
      }
    } catch (error) {
      console.error('Logout failed:', error)
      throw error
    }
  }

  /**
   * Refresh authentication token
   * BFF Pattern: Token refresh is handled server-side via the session cookie
   * Client does not need to manage token refresh - it's automatic
   *
   * @deprecated Client should not call this method in BFF pattern
   * Token refresh happens automatically server-side when session is accessed
   */
  async refreshToken(): Promise<{ accessToken: string; expiresIn: number }> {
    // BFF Pattern: Token refresh is automatic server-side
    // This method exists only for backward compatibility
    console.warn('refreshToken() is deprecated in BFF pattern - refresh happens automatically server-side')

    return {
      accessToken: '', // Not exposed to client in BFF pattern
      expiresIn: 3600, // Placeholder value
    }
  }

  /**
   * Ask auth-service to email a password reset link (agion/Agion#2375).
   *
   * auth-service answers 202 for every well-formed address, whether or not it
   * has a password account, so the caller shows one confirmation for all of
   * them. A refusal (400 invalid_request, 429 with Retry-After) throws
   * AuthRequestError; show it with passwordResetErrorMessage.
   */
  async forgotPassword(data: ForgotPasswordFormData): Promise<{ message: string }> {
    const response = await fetchRetryingWhenBusy(() => fetch(`${this.authServiceUrl}/auth/password-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        email: data.email,
      }),
    }))

    if (!response.ok) {
      throw await AuthRequestError.fromResponse(response)
    }

    const result: unknown = await response.json().catch(() => null)
    const message = result !== null && typeof result === 'object' && 'message' in result && typeof result.message === 'string'
      ? result.message
      : ''
    return { message }
  }

  /**
   * Set a new password with the token from the emailed link (agion/Agion#2375).
   *
   * Resolves on 200 {"status":"password_reset"}. Refusals throw AuthRequestError
   * with serverError invalid_token, password_rejected (message = the policy's
   * sentence) or password_reused; a busy auth-service (503 busy_retry_later) is
   * retried after Retry-After before it is reported.
   */
  async resetPassword(data: Pick<ResetPasswordFormData, 'token' | 'password'>): Promise<void> {
    const response = await fetchRetryingWhenBusy(() => fetch(`${this.authServiceUrl}/auth/password-reset/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        token: data.token,
        password: data.password,
      }),
    }))

    if (!response.ok) {
      throw await AuthRequestError.fromResponse(response)
    }
  }

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${this.authServiceUrl}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Email verification failed')
      }

      const result = await response.json()

      return {
        message: result.message || 'Email verified successfully',
      }
    } catch (error) {
      console.error('Email verification failed:', error)
      throw error
    }
  }

  /**
   * Setup MFA
   * BFF Pattern: Authentication via session cookie, not localStorage token
   */
  async setupMFA(method: string): Promise<{ qrCode?: string; secret?: string; backupCodes?: string[] }> {
    try {
      const response = await fetch(`${this.authServiceUrl}/auth/mfa/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // BFF Pattern: Send session cookie for authentication
        body: JSON.stringify({
          method,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'MFA setup failed')
      }

      const result = await response.json()

      return {
        qrCode: result.qr_code,
        secret: result.secret,
        backupCodes: result.backup_codes,
      }
    } catch (error) {
      console.error('MFA setup failed:', error)
      throw error
    }
  }

  /**
   * Verify MFA code
   * BFF Pattern: After MFA verification, session is established via HttpOnly cookie
   */
  async verifyMFA(data: MFAFormData & { csrfToken: string }): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.authServiceUrl}/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // BFF Pattern: Receive session cookie
        body: JSON.stringify({
          challenge: data.challenge,
          method: data.method,
          code: data.code,
          mfa_token: data.mfa_token,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'MFA verification failed')
      }

      const result = await response.json()

      if (result.redirect_to) {
        return {
          redirect_to: result.redirect_to,
          success: true,
        }
      }

      // BFF Pattern: Tokens in response are deprecated
      // Session cookie (agion_sso_session) is set by backend
      return {
        success: true,
        // Legacy fields - will be removed when backend stops sending them
        access_token: result.access_token,
        refresh_token: result.refresh_token,
        expires_in: result.expires_in,
      }
    } catch (error) {
      console.error('MFA verification failed:', error)
      throw error
    }
  }

  /**
   * Validate session
   * BFF Pattern: Session is validated via HttpOnly cookie, not localStorage token
   */
  async validateSession(): Promise<{ valid: boolean; user?: unknown }> {
    try {
      // BFF Pattern: Session cookie is sent automatically with credentials: 'include'
      const response = await fetch(`${this.authServiceUrl}/session/info`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send agion_sso_session cookie
      })

      if (!response.ok) {
        return { valid: false }
      }

      const result = await response.json()

      return {
        valid: true,
        user: result,
      }
    } catch (error) {
      console.error('Session validation failed:', error)
      return { valid: false }
    }
  }

  /**
   * Handle OAuth2 authorization code exchange
   *
   * @deprecated This method is deprecated - use @agion/oidc-client instead
   * BFF Pattern: Token exchange should happen server-side, not in the client
   */
  async handleOAuth2Callback(code: string, state: string, codeVerifier: string): Promise<AuthResponse> {
    console.warn('handleOAuth2Callback() is deprecated - use @agion/oidc-client with BFF pattern')

    try {
      const response = await fetch(`${config.authAuthority}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        credentials: 'include', // BFF Pattern: Receive session cookie
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: 'login-ui',
          code,
          redirect_uri: config.redirectUri,
          code_verifier: codeVerifier,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'OAuth authentication failed')
      }

      const result = await response.json()

      // BFF Pattern: These tokens should NOT be used by client
      // Session cookie (agion_sso_session) is set by backend
      return {
        success: true,
        // Legacy fields - will be removed when backend stops sending them
        access_token: result.access_token,
        refresh_token: result.refresh_token,
        expires_in: result.expires_in,
        id_token: result.id_token,
        token_type: result.token_type || 'Bearer',
      }
    } catch (error) {
      console.error('OAuth2 token exchange failed:', error)
      throw error
    }
  }

  /**
   * Handle OAuth redirect (legacy method - maintained for compatibility)
   * BFF Pattern: OAuth callback should establish session via HttpOnly cookie
   */
  async handleOAuthRedirect(provider: string, code: string, state: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.authServiceUrl}/oauth2/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include', // BFF Pattern: Receive session cookie
        body: JSON.stringify({
          provider,
          code,
          state,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'OAuth authentication failed')
      }

      const result = await response.json()

      // BFF Pattern: Tokens in response are deprecated
      // Session cookie (agion_sso_session) is set by backend
      return {
        success: true,
        // Legacy fields - will be removed when backend stops sending them
        access_token: result.access_token,
        refresh_token: result.refresh_token,
        expires_in: result.expires_in,
        user: result.user,
      }
    } catch (error) {
      console.error('OAuth authentication failed:', error)
      throw error
    }
  }

  // Consent flow methods

  /**
   * Get consent challenge details from Auth Service (custom OIDC provider)
   */
  async getConsentChallenge(challenge: string): Promise<ConsentChallenge> {
    try {
      const response = await fetch(`${this.authServiceUrl}/oauth2/consent/requests/${challenge}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Failed to get consent challenge: ${response.statusText}`)
      }

      const data = await response.json()

      return {
        challenge,
        csrfToken: '',
        requestUrl: data.request_url || '',
        skip: data.skip || false,
        subject: data.subject || '',
        client: {
          id: data.client.client_id,
          name: data.client.client_name || data.client.client_id,
          description: data.client.description || '',
          logo_uri: data.client.logo_uri,
          policy_uri: data.client.policy_uri,
          tos_uri: data.client.tos_uri,
        },
        requestedScopes: data.requested_scopes || [],
        requestedAccessTokenAudience: data.requested_audience || [],
        oidcContext: data.oidc_context || {},
      }
    } catch (error) {
      console.error('Failed to get consent challenge:', error)
      throw new Error('Failed to get consent challenge')
    }
  }

  /**
   * Submit consent decision to Auth Service (custom OIDC provider)
   */
  async submitConsent(data: ConsentData & { csrfToken: string; accept: boolean }): Promise<{ redirectTo: string }> {
    try {
      const response = await fetch(`${this.authServiceUrl}/oauth2/consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': data.csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          challenge: data.challenge,
          accept: data.accept,
          granted_scopes: data.accept ? data.granted_scopes : [],
          granted_audience: data.accept ? data.granted_audience : [],
          remember: data.remember,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Consent submission failed')
      }

      const result = await response.json()

      return {
        redirectTo: result.redirect_to,
      }
    } catch (error) {
      console.error('Consent submission failed:', error)
      throw error
    }
  }

  // Utility methods

  /**
   * Get available MFA methods
   */
  async getMFAMethods(): Promise<MFAMethod[]> {
    try {
      const response = await fetch(`${this.authServiceUrl}/auth/mfa/methods`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        return [
          { id: 'totp', type: 'totp' as const, label: 'TOTP Authenticator', enabled: true },
          { id: 'sms', type: 'sms' as const, label: 'SMS', enabled: true },
          { id: 'email', type: 'email' as const, label: 'Email', enabled: true },
        ]
      }

      const result = await response.json()
      return result.methods || []
    } catch (error) {
      console.error('Failed to get MFA methods:', error)
      return []
    }
  }

  /**
   * Get UI configuration
   */
  async getUIConfig(): Promise<UIConfig> {
    try {
      const response = await fetch(`${this.authServiceUrl}/ui/config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        // Return default config if API fails
        return {
          theme: {
            primaryColor: '#2563eb',
            secondaryColor: '#64748b',
          },
          features: {
            registration: true,
            passwordReset: true,
            rememberMe: true,
            socialLogin: false,
            mfa: true,
          },
          branding: {
            companyName: 'Agion Platform',
            supportEmail: 'support@agion.ai',
          },
        }
      }

      const result = await response.json()
      return result.config || {}
    } catch (error) {
      console.error('Failed to get UI config:', error)
      // Return default config if API fails
      return {
        theme: {
          primaryColor: '#2563eb',
          secondaryColor: '#64748b',
        },
        features: {
          registration: true,
          passwordReset: true,
          rememberMe: true,
          socialLogin: false,
          mfa: true,
        },
        branding: {
          companyName: 'Agion Platform',
          supportEmail: 'support@agion.ai',
        },
      }
    }
  }

  /**
   * Generate OAuth2 authorization URL with PKCE
   * Note: This method is deprecated - use @agion/oidc-client instead
   */
  generateOAuth2URL(state: string, codeChallenge: string): string {
    const params = new URLSearchParams({
      client_id: 'login-ui',
      redirect_uri: config.redirectUri,
      scope: 'openid profile email',
      response_type: 'code',
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    return `${config.authAuthority}/oauth2/auth?${params.toString()}`;
  }

  /**
   * Generate OAuth URL (legacy method - maintained for compatibility)
   */
  generateOAuthURL(provider: string, state: string, challenge?: string | null): string {
    const params = new URLSearchParams({ state })
    if (challenge) {
      params.set('login_challenge', challenge)
    }
    return `${this.authServiceUrl}/oauth2/${provider}?${params.toString()}`
  }

  /**
   * Generate PKCE code verifier and challenge
   */
  async generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
    const codeVerifier = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
      .replace(/[+/]/g, (m) => ({ '+': '-', '/': '_' }[m] || m))
      .replace(/=/g, '');

    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/[+/]/g, (m) => ({ '+': '-', '/': '_' }[m] || m))
      .replace(/=/g, '');
    return { codeVerifier, codeChallenge };
  }

  /**
   * WebAuthn registration
   * BFF Pattern: Authentication via session cookie, not localStorage token
   */
  async registerWebAuthn(): Promise<{ challenge: string; user: unknown }> {
    try {
      const response = await fetch(`${this.authServiceUrl}/auth/webauthn/register/begin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // BFF Pattern: Send session cookie for authentication
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'WebAuthn registration failed')
      }

      const result = await response.json()

      return {
        challenge: result.challenge,
        user: result.user,
      }
    } catch (error) {
      console.error('WebAuthn registration failed:', error)
      throw error
    }
  }

  /**
   * WebAuthn authentication
   * BFF Pattern: After WebAuthn authentication, session is established via HttpOnly cookie
   */
  async authenticateWebAuthn(challenge: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.authServiceUrl}/auth/webauthn/login/begin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // BFF Pattern: Receive session cookie
        body: JSON.stringify({
          challenge,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'WebAuthn authentication failed')
      }

      const result = await response.json()

      // BFF Pattern: Tokens in response are deprecated
      // Session cookie (agion_sso_session) is set by backend
      return {
        success: true,
        // Legacy fields - will be removed when backend stops sending them
        access_token: result.access_token,
        refresh_token: result.refresh_token,
        expires_in: result.expires_in,
      }
    } catch (error) {
      console.error('WebAuthn authentication failed:', error)
      throw error
    }
  }

  // P-009: Invitation methods

  /**
   * Validate an invitation token
   */
  async validateInvitation(token: string): Promise<{
    valid: boolean;
    email?: string;
    organizationName?: string;
    role?: string;
    inviterName?: string;
    expiresAt?: string;
    requiresVerificationCode: boolean;
    userExists?: boolean;
    errorCode?: string;
  }> {
    try {
      const response = await fetch(`${this.authServiceUrl}/v1/invitations/validate?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        // Backend now returns JSON errors with error codes
        let errorCode = 'unknown';
        try {
          const errorData = await response.json();
          errorCode = errorData.error || 'unknown';
        } catch {
          // Fallback if response isn't JSON
        }
        return {
          valid: false,
          requiresVerificationCode: false,
          errorCode,
        };
      }

      // Backend returns snake_case, convert to camelCase
      const data = await response.json();
      return {
        valid: data.valid,
        email: data.email,
        organizationName: data.organization_name,
        role: data.role,
        inviterName: data.inviter_name,
        expiresAt: data.expires_at,
        requiresVerificationCode: data.requires_verification_code ?? false,
        userExists: data.user_exists ?? false,
      };
    } catch (error) {
      console.error('Failed to validate invitation:', error);
      return {
        valid: false,
        requiresVerificationCode: false,
        errorCode: 'network_error',
      };
    }
  }

  /**
   * Register a new user with an invitation (P-009)
   * This is for users who don't have an account yet but received an invitation
   */
  async registerWithInvitation(data: {
    token: string;
    verificationCode: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<AuthResponse> {
    return registerWithInvitation(this.authServiceUrl, data);
  }

  /**
   * Accept an invitation after password authentication established the BFF
   * session. OAuth callbacks accept their invitation from server-side state.
   * Returns the organisation joined and its home as the server names them
   * (ADR-20260930 D7). A field the answer lacks is left out.
   */
  async acceptInvitation(token: string): Promise<InvitationAcceptance> {
    const response = await fetch(`${this.apiUrl}/api/v1/invitations/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = typeof errorData.error === 'string'
        ? errorData.error
        : errorData.error?.message || errorData.message || 'Failed to accept invitation';
      throw new Error(message);
    }

    // The accept answer is snake_case JSON.
    const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    const text = (key: string) => (typeof data[key] === 'string' && data[key] !== '' ? (data[key] as string) : undefined);
    return { organizationId: text('organization_id'), homeApp: text('home_app'), homeUrl: text('home_url') };
  }

  /**
   * Move the signed-in session to another organisation the member belongs
   * to. True when the server accepted the switch.
   */
  async switchOrganization(organizationId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/v1/users/me/switch-organization`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ organization_id: organizationId }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Resend the 6-digit verification code for an invitation
   */
  async resendVerificationCode(token: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.authServiceUrl}/v1/invitations/resend-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to resend code' }));
      throw new Error(errorData.message || 'Failed to resend verification code');
    }

    return response.json();
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
